'use client'

import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FaChevronDown, FaDownload } from 'react-icons/fa'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { TbHistoryToggle } from 'react-icons/tb'
import ClipLoader from 'react-spinners/ClipLoader'
import { useTranslation } from 'react-i18next'
import { CreateContext } from '../../../contexts/CreateContext'
import { useAuth } from '../../../contexts/AuthContext'
import {
  flameHighDetailGems,
  flameImageGems,
  getModelName,
} from '../../../helpers/helpers'
import Inputs from '../../../component/Inputs'
import InputsVideo from '../../../component/InputsVideo'
import ModelModal from '../../../component/ModelModal'
import PromptBar from '../../../component/PromptBar'
import SettingsDrawer from '../../../component/SettingsDrawer'
import { GenerateModeContext } from '../../../component/GenerateEmptyState'

// useLayoutEffect on the client (position before paint, no flicker); useEffect on
// the server to avoid the SSR useLayoutEffect warning.
const useIsoLayoutEffect =
  typeof document !== 'undefined' ? useLayoutEffect : useEffect

// Session "Save all" control. Visible whenever there's anything saveable. A
// main-bg-colour notification bubble shows how many results are still unsaved
// (and disappears once everything is saved/published).
function SaveAllButton({ bridge, t }) {
  const unsavedCount = bridge.unsavedCount || 0
  return (
    <button
      type="button"
      onClick={bridge.onDownloadAll}
      disabled={bridge.downloadingAll}
      title={t('downloadAllSession')}
      aria-label={t('downloadAllSession')}
      className="pointer-events-auto relative h-11 w-11 flex items-center justify-center rounded-full bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] text-white transition-colors shadow-lg disabled:opacity-60 disabled:pointer-events-none"
    >
      {bridge.downloadingAll ? (
        <ClipLoader color="#FFF" size={16} />
      ) : (
        <FaDownload size={16} />
      )}
      {unsavedCount > 0 && !bridge.downloadingAll && (
        <span className="absolute -top-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--main-bg-color)] px-1 text-[0.6rem] font-bold leading-none text-white shadow">
          {unsavedCount > 99 ? '99+' : unsavedCount}
        </span>
      )}
    </button>
  )
}

// Easing for the feed's scroll animations (slide-up reveal + nudge hint).
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3)
const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

// Locate a feed card by its item id. Avoids a `[data-feed-item-id="…"]` querySelector
// because ids can be Firestore docRefs containing characters a CSS selector would
// choke on; matching the attribute value in JS sidesteps all escaping.
const findFeedItem = (scrollEl, itemId) => {
  if (!scrollEl || !itemId) return null
  const nodes = scrollEl.querySelectorAll('[data-feed-item-id]')
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].getAttribute('data-feed-item-id') === itemId) return nodes[i]
  }
  return null
}

// Variant B: presentational shell for the mobile-app-style /generate UI. Generation
// logic, the legacy result cards (`gallery`), and the modal stack live in Create.jsx
// and arrive via props — `bridge` carries the real handlers + modal setters.
export default function CreateSimplified({
  gallery,
  bridge = {},
  revealNonce = 0,
  revealAnchorId = null,
  feedCount = 0,
}) {
  const { t } = useTranslation()
  const c = useContext(CreateContext)
  const { user, subscription, banned, country, newUser } = useAuth()
  const isAdmin = user?.uid === process.env.NEXT_PUBLIC_ADMIN_UID

  // Desktop panel is always visible (CSS); `drawerOpen` only drives the mobile
  // full-screen overlay, so it starts closed.
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [styleOpen, setStyleOpen] = useState(false)
  const [settingsTab, setSettingsTab] = useState('image')
  // animateMode now lives in CreateContext (shared with the Animate modal + submit).

  // Mirror c.formatTab onto the local settings tab: cloned video → Animate panel,
  // and the persisted last-used tab restored from cloneData on refresh. Manual
  // toggling also writes formatTab (see modeToggle), so the two never diverge.
  useEffect(() => {
    setSettingsTab(c.formatTab === 'video' ? 'animate' : 'image')
  }, [c.formatTab])

  // --- Mobile-style feed animations (promptchan-mobile parity) -----------------
  // A new generation slides UP into place — the previously-top card slides down out
  // of the way — instead of popping in at the top. And once there are a couple of
  // results, a one-time "nudge" peeks the feed down to hint the stack is scrollable.
  const feedScrollRef = useRef(null)
  const promptBarRef = useRef(null)
  const hintShownRef = useRef(false)
  // Previous non-pending result count, so the nudge fires exactly on the 1 → 2
  // transition (the "second generation") and not when a feed is rehydrated from cache.
  const prevFeedCountRef = useRef(0)
  // The in-flight slide reveal. A single insertion can request a reveal twice (the
  // direct caller + the growth effect in Create); this lets the second one no-op
  // instead of restarting the slide mid-flight.
  const slideRef = useRef(null)

  // Slide-up reveal. `revealNonce` bumps (from Create) only when a request starts or
  // a finished result lands on top — never on delete. `revealAnchorId` is the card
  // that was on top a moment ago: we place it at the viewport top (new card off-screen
  // just above), then smooth-scroll to 0 so the new card rises into place. If that
  // card is gone (a result replacing its own loader), there's nothing above to reveal,
  // so we simply settle at the top with no slide — as on a brand-new first generation.
  useIsoLayoutEffect(() => {
    if (revealNonce === 0) return
    const el = feedScrollRef.current
    if (!el) return

    // Duplicate reveal for the same insertion: let the running slide finish.
    if (
      slideRef.current?.active &&
      slideRef.current.anchorId === revealAnchorId
    ) {
      return
    }
    if (slideRef.current) slideRef.current.cancelled = true // supersede any prior slide

    const reduce = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches
    const anchor = findFeedItem(el, revealAnchorId)
    const shift = anchor
      ? Math.max(
          0,
          anchor.getBoundingClientRect().top -
            el.getBoundingClientRect().top +
            el.scrollTop,
        )
      : 0

    if (reduce || shift <= 1) {
      el.scrollTop = 0
      slideRef.current = null
      return
    }

    const previousSnapType = el.style.scrollSnapType
    el.style.scrollSnapType = 'none'
    el.scrollTop = shift // previous card at the top, new card sitting just above it

    const ctrl = {
      anchorId: revealAnchorId,
      active: true,
      cancelled: false,
      raf: 0,
    }
    slideRef.current = ctrl
    const duration = 380
    const startTime = performance.now()
    const tick = (now) => {
      if (ctrl.cancelled) {
        el.style.scrollSnapType = previousSnapType
        return
      }
      const elapsed = Math.min((now - startTime) / duration, 1)
      el.scrollTop = shift * (1 - easeOutCubic(elapsed))
      if (elapsed < 1) {
        ctrl.raf = requestAnimationFrame(tick)
        return
      }
      el.scrollTop = 0
      el.style.scrollSnapType = previousSnapType
      ctrl.active = false
    }
    ctrl.raf = requestAnimationFrame(tick)
  }, [revealNonce, revealAnchorId])

  // Cancel an in-flight slide on unmount.
  useEffect(
    () => () => {
      if (slideRef.current) {
        slideRef.current.cancelled = true
        cancelAnimationFrame(slideRef.current.raf)
      }
    },
    [],
  )

  // "Scroll down" nudge (promptchan-mobile parity): the moment the stack reaches its
  // SECOND card this session — i.e. the user just generated a second image — peek the
  // feed down a little and ease it back, hinting there's more below. Once per session
  // (re-arms if the stack drops back under 2). Fires on the 1 → 2 transition only, so a
  // feed rehydrated from cache (0 → N) stays quiet, and only when the newest card is in
  // view at fire time, so we never yank someone who has scrolled away.
  useEffect(() => {
    const el = feedScrollRef.current
    const prevCount = prevFeedCountRef.current
    prevFeedCountRef.current = feedCount

    if (feedCount < 2) {
      hintShownRef.current = false // re-arm for the next fresh build-up
      return
    }
    if (!el || hintShownRef.current || prevCount < 1) return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    hintShownRef.current = true

    const peek = Math.min(el.clientHeight * 0.12, 72)
    const previousSnapType = el.style.scrollSnapType
    let raf = 0
    let cancelled = false
    const RISE = 450
    const FALL = 550

    const timer = setTimeout(() => {
      // Only nudge if the newest card is on screen and there's actually room to scroll.
      if (el.scrollTop > 4 || el.scrollHeight - el.clientHeight < peek) return
      el.style.scrollSnapType = 'none'
      const startTime = performance.now()
      const tick = (now) => {
        if (cancelled) {
          el.scrollTop = 0
          el.style.scrollSnapType = previousSnapType
          return
        }
        const e = now - startTime
        if (e < RISE) {
          el.scrollTop = peek * easeOutCubic(e / RISE)
        } else if (e < RISE + FALL) {
          el.scrollTop = peek * (1 - easeInOutCubic((e - RISE) / FALL))
        } else {
          el.scrollTop = 0
          el.style.scrollSnapType = previousSnapType
          return
        }
        raf = requestAnimationFrame(tick)
      }
      raf = requestAnimationFrame(tick)
    }, 900)

    return () => {
      cancelled = true
      clearTimeout(timer)
      cancelAnimationFrame(raf)
      el.style.scrollSnapType = previousSnapType
    }
  }, [feedCount])

  // Native-app lock while B is mounted: the create screen fills the viewport, the
  // global footer sits below the fold, and the page behind can't scroll/bounce.
  // Class-based (see index.css) rather than body.style.overflow because the shared
  // modals reset that inline style to 'unset' on close, which used to re-enable page
  // scroll mid-session. Also disables double-tap zoom + iOS focus auto-zoom.
  useEffect(() => {
    const html = document.documentElement
    const scrollY = window.scrollY
    document.body.style.setProperty('--pc-native-lock-top', `-${scrollY}px`)
    html.classList.add('pc-native-lock')
    document.body.classList.add('pc-native-lock')
    // Document-level guard for gestures that start outside body (address-bar edges);
    // pinch zoom stays available for accessibility.
    const prevOverscroll = html.style.overscrollBehavior
    const prevTouchAction = html.style.touchAction
    const prevScrollBehavior = html.style.scrollBehavior
    html.style.overscrollBehavior = 'none'
    html.style.touchAction = 'manipulation'
    html.style.scrollBehavior = 'auto'
    return () => {
      html.classList.remove('pc-native-lock')
      document.body.classList.remove('pc-native-lock')
      document.body.style.removeProperty('--pc-native-lock-top')
      html.style.overscrollBehavior = prevOverscroll
      html.style.touchAction = prevTouchAction
      html.style.scrollBehavior = prevScrollBehavior
      window.scrollTo(0, scrollY)
    }
  }, [])

  // Position the fixed view below the site header. On Tailwind 3 `pt-26` isn't a real
  // class so header height can't be assumed — measure the actual <header> and react
  // to resizes/banners.
  const [headerOffset, setHeaderOffset] = useState(0)
  useEffect(() => {
    const header = document.querySelector('header')
    const measure = () =>
      setHeaderOffset(
        header ? Math.round(header.getBoundingClientRect().height) : 0,
      )
    measure()
    const t = setTimeout(measure, 300) // catch late header / banner mount
    window.addEventListener('resize', measure)
    let ro
    if (header && 'ResizeObserver' in window) {
      ro = new ResizeObserver(measure)
      ro.observe(header)
    }
    return () => {
      clearTimeout(t)
      window.removeEventListener('resize', measure)
      ro?.disconnect()
    }
  }, [])

  // Publish feed geometry as CSS vars the feed reads:
  //  --feed-media-max      height cap for the media (the visible band)
  //  --feed-page-height    one full scroll-viewport (phone = one TikTok page)
  //  --feed-top-reserve    space the phone-only ⋮ + model bar overlays
  //  --feed-bottom-reserve space the prompt card overlays
  // On phones each item is a full-height page with the media centered between the
  // reserves (one generation at a time, never under the prompt box). From md up the
  // feed free-scrolls so only the media cap matters.
  useIsoLayoutEffect(() => {
    const scroll = feedScrollRef.current
    if (!scroll) return
    const apply = () => {
      const ch = scroll.clientHeight
      // 768px — must track the md: classes below (static panel, no ⋮ bar to
      // reserve for) and the header's own md breakpoint.
      const panelStatic = window.matchMedia('(min-width: 768px)').matches
      const barH = promptBarRef.current?.getBoundingClientRect().height || 132
      // Top bar (h-11 + py-3 ≈ 68 → 72 for clearance) and prompt card (+16).
      const topReserve = 72
      const bottomReserve = Math.round(barH + 16)
      const avail = panelStatic
        ? ch - 24 - barH - 8
        : ch - topReserve - bottomReserve
      // Media fills the band exactly. No minimum floor: on short/landscape phones a
      // floor above the band would overflow the centered page and slide the media
      // back under the bars. Clamp non-negative for degenerate viewports.
      scroll.style.setProperty(
        '--feed-media-max',
        `${Math.max(0, Math.round(avail))}px`,
      )
      scroll.style.setProperty('--feed-page-height', `${ch}px`)
      scroll.style.setProperty('--feed-top-reserve', `${topReserve}px`)
      scroll.style.setProperty('--feed-bottom-reserve', `${bottomReserve}px`)
    }
    apply()
    window.addEventListener('resize', apply)
    let ro
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(apply)
      ro.observe(scroll)
      if (promptBarRef.current) ro.observe(promptBarRef.current)
    }
    return () => {
      window.removeEventListener('resize', apply)
      ro?.disconnect()
    }
  }, [headerOffset])

  const gemCost = useMemo(() => {
    const { model, ntier, faceFix, charImage, quality, characterVersion } = c
    if (!model) return 0
    // Flame: the model's own price (flameImageGems mirrors the backend's gem_cost) + its
    // paid High detail render at Max + the same AI Character surcharge.
    if (model.includes('Flame'))
      return (
        ntier * flameImageGems(model) +
        (quality === 'Max' ? ntier * flameHighDetailGems(model) : 0) +
        (charImage ? (characterVersion === 'v3' ? 3 : 1) : 0)
      )
    // AI Character costs +3 on v3 (strongest apply, the Qwen step), otherwise
    // +1 — mirrors the per-version cost badge in Inputs.
    return (
      ntier +
      (faceFix ? 1 : 0) +
      (charImage ? (characterVersion === 'v3' ? 3 : 1) : 0) +
      (model.includes('XL') ? ntier : 0) +
      (quality === 'Extreme' ? 1 : quality === 'Max' ? 2 : 0)
    )
  }, [c])

  // Mirrors Create.jsx's <Inputs /> prop bag; modal setters that are component-local
  // in Create arrive via `bridge`.
  const inputsProps = {
    model: c.model,
    setModel: c.setModel,
    imageSize: c.imageSize,
    setImageSize: c.setImageSize,
    quality: c.quality,
    setQuality: c.setQuality,
    creativity: c.creativity,
    setCreativity: c.setCreativity,
    prompt: c.prompt,
    setPrompt: c.setPrompt,
    negativePrompt: c.negativePrompt,
    setNegativePrompt: c.setNegativePrompt,
    seed: c.seed,
    setSeed: c.setSeed,
    handleSubmit: bridge.onGenerate,
    subscription,
    isLoading: bridge.imageAtConcurrencyLimit,
    lora: c.lora,
    setLora: c.setLora,
    lorasList: c.lorasList,
    setLorasList: c.setLorasList,
    locked: c.locked,
    setLocked: c.setLocked,
    ntier: c.ntier,
    setNtier: c.setNtier,
    faceFix: c.faceFix,
    setFaceFix: c.setFaceFix,
    chillMode: c.chillMode,
    setChillMode: c.setChillMode,
    cooldown: c.cooldown,
    cooldownProgress: c.cooldownProgress,
    setImageSrc: c.setImageSrc,
    setImageHistory: c.setImageHistory,
    filter: c.filter,
    setFilter: c.setFilter,
    details: c.details,
    setDetails: c.setDetails,
    customPoseImage: c.customPoseImage,
    setCustomPoseImage: c.setCustomPoseImage,
    setIsLimitModalOpen: bridge.setIsLimitModalOpen,
    setErrorText: c.setErrorText,
    charImage: c.charImage,
    setCharImage: c.setCharImage,
    recentCharacters: c.recentCharacters,
    setIsErrorModalOpen: bridge.setIsErrorModalOpen,
    characterSuggestion: c.characterSuggestion,
    setCharacterSuggestion: c.setCharacterSuggestion,
    locationSuggestion: c.locationSuggestion,
    setLocationSuggestion: c.setLocationSuggestion,
    actionSuggestion: c.actionSuggestion,
    setActionSuggestion: c.setActionSuggestion,
    stickMode: c.stickMode,
    setStickMode: c.setStickMode,
    emotion: c.emotion,
    setEmotion: c.setEmotion,
    characterVersion: c.characterVersion,
    setCharacterVersion: c.setCharacterVersion,
    banned,
    folderSetting: c.folderSetting,
    setFolderSetting: c.setFolderSetting,
    folders: c.folders,
    setFolders: c.setFolders,
    autoSave: c.autoSave,
    setAutoSave: c.setAutoSave,
    age: c.age,
    setAge: c.setAge,
    showAttributes: c.showAttributes,
    setShowAttributes: c.setShowAttributes,
    weight: c.weight,
    setWeight: c.setWeight,
    breasts: c.breasts,
    setBreasts: c.setBreasts,
    ass: c.ass,
    setAss: c.setAss,
    penis: c.penis,
    setPenis: c.setPenis,
    nsfwEnabled: c.nsfwEnabled,
    setNsfwEnabled: c.setNsfwEnabled,
    visibilityMode: c.visibilityMode,
    setVisibilityMode: c.setVisibilityMode,
    setHasUnsavedChanges: bridge.setHasUnsavedChanges,
    setBookmarkModalOpen: bridge.setBookmarkModalOpen,
    bookmarkModalOpen: bridge.bookmarkModalOpen,
  }

  // Mirrors Create.jsx's <InputsVideo /> prop bag.
  const inputsVideoProps = {
    model: c.videoModel,
    setModel: c.setVideoModel,
    imageSize: c.videoImageSize,
    setImageSize: c.setVideoImageSize,
    quality: c.quality,
    setQuality: c.setQuality,
    creativity: c.videoCreativity,
    setCreativity: c.setVideoCreativity,
    prompt: c.videoPrompt,
    setPrompt: c.setVideoPrompt,
    negativePrompt: c.videoNegativePrompt,
    setNegativePrompt: c.setVideoNegativePrompt,
    seed: c.seedVideo,
    setSeed: c.setSeedVideo,
    handleSubmitVideoV3: bridge.handleSubmitVideoV3,
    subscription,
    isLoading: c.videoIsLoading,
    lora: c.videoLora,
    setLora: c.setVideoLora,
    locked: c.locked,
    setLocked: c.setLocked,
    ntier: c.ntier,
    setNtier: c.setNtier,
    faceFix: c.faceFix,
    setFaceFix: c.setFaceFix,
    chillMode: c.chillMode,
    setChillMode: c.setChillMode,
    isAdmin,
    safeImage: c.safeImage,
    setSafeImage: c.setSafeImage,
    watermark: c.watermark,
    setWatermark: c.setWatermark,
    cooldown: c.cooldown,
    cooldownProgress: c.cooldownProgress,
    setImageSrc: c.setImageSrc,
    setImageHistory: c.setImageHistory,
    filter: c.videoFilter,
    setFilter: c.setVideoFilter,
    details: c.details,
    setDetails: c.setDetails,
    customPoseImage: c.customPoseImage,
    setCustomPoseImage: c.setCustomPoseImage,
    setIsLimitModalOpen: bridge.setIsLimitModalOpen,
    setErrorText: c.setErrorText,
    charImage: c.charImageVideo,
    setCharImage: c.setCharImageVideo,
    recentCharacters: c.recentCharacters,
    setIsErrorModalOpen: bridge.setIsErrorModalOpen,
    animation: c.animation,
    setAnimation: c.setAnimation,
    cameraMotion: c.cameraMotion,
    setCameraMotion: c.setCameraMotion,
    videoRefImage: c.videoRefImage,
    setVideoRefImage: c.setVideoRefImage,
    setVideoRefLora: c.setVideoRefLora,
    setVideoId: c.setVideoId,
    videoVersion: c.videoVersion,
    setVideoVersion: c.setVideoVersion,
    videoModelMode: c.videoModelMode,
    setVideoModelMode: c.setVideoModelMode,
    dialogue: c.dialogue,
    setDialogue: c.setDialogue,
    dialogueSpeaker: c.dialogueSpeaker,
    setDialogueSpeaker: c.setDialogueSpeaker,
    dialogueTone: c.dialogueTone,
    setDialogueTone: c.setDialogueTone,
    dialogueAccent: c.dialogueAccent,
    setDialogueAccent: c.setDialogueAccent,
    videoLength: c.videoLength,
    setVideoLength: c.setVideoLength,
    videoFps: c.videoFps,
    setVideoFps: c.setVideoFps,
    videoResolution: c.videoResolution,
    setVideoResolution: c.setVideoResolution,
    cameraMove: c.cameraMove,
    setCameraMove: c.setCameraMove,
    videoQuality: c.videoQuality,
    setVideoQuality: c.setVideoQuality,
    videoImageSizeV2: c.videoImageSizeV2,
    setVideoImageSizeV2: c.setVideoImageSizeV2,
    videoProgress: c.videoProgress,
    queueLength: c.queueLength,
    setQueueLength: c.setQueueLength,
    age: c.ageVideo,
    setAge: c.setAgeVideo,
    weight: c.weightVideo,
    setWeight: c.setWeightVideo,
    breasts: c.breastsVideo,
    setBreasts: c.setBreastsVideo,
    ass: c.assVideo,
    setAss: c.setAssVideo,
    showAttributes: c.showAttributesVideo,
    setShowAttributes: c.setShowAttributesVideo,
    videoFramerateV2: c.videoFramerateV2,
    setVideoFramerateV2: c.setVideoFramerateV2,
    loop: c.loop,
    setLoop: c.setLoop,
    country,
    animateMode: c.animateMode,
    setAnimateMode: c.setAnimateMode,
    audioEnabled: c.audioEnabled,
    setAudioEnabled: c.setAudioEnabled,
    animateVersion: c.animateVersion,
    setAnimateVersion: c.setAnimateVersion,
    setVideoRefId: c.setVideoRefId,
    setVideoSrc: c.setVideoSrc,
    // The Video tab is text-to-video, so InputsVideo renders its prompt-mode config
    // (model/character/attributes/orientation/quality/audio). The prompt itself is in
    // the bottom PromptBar (textToVideoOnly hides InputsVideo's own textarea).
    videoStartingPoint: 'prompt',
    setVideoStartingPoint: c.setVideoStartingPoint,
    setFormatTab: c.setFormatTab,
    setExtendModalOpen: bridge.setExtendModalOpen,
    visibilityMode: c.visibilityMode,
    setVisibilityMode: c.setVisibilityMode,
    setHasUnsavedChanges: bridge.setHasUnsavedChanges,
    lorasList: c.lorasListVideo,
    setLorasList: c.setLorasListVideo,
    setBookmarkModalOpen: bridge.setBookmarkModalOpen,
    bookmarkModalOpen: bridge.bookmarkModalOpen,
    banned,
    folderSetting: c.folderSetting,
    setFolderSetting: c.setFolderSetting,
    folders: c.folders,
    setFolders: c.setFolders,
    autoSave: c.autoSave,
    setAutoSave: c.setAutoSave,
  }

  const getLocalizedModelLabel = (model) => {
    const displayName = getModelName(model) || model
    const displayKey = `modelDisplay${displayName
      .replace(/\+/g, 'Plus')
      .replace(/[^A-Za-z0-9]/g, '')}`
    return t(displayKey, t(model?.toLowerCase(), displayName))
  }

  // Passed as headerLeft so it shares the panel's header row with the bookmark/reset
  // buttons.
  const modeToggle = (
    <div className="flex gap-1.5 p-1 rounded-2xl bg-[var(--background-bg-color)]">
      {[
        { key: 'image', label: t('image') },
        // The Video tab carries the pill — it's what points people at the tab in the first
        // place. It used to read "v5!", but there is no v5 in the UI any more: the model
        // switcher is Auto / v4 / v4-voice, so a version number on the tab pointed at nothing.
        // t('new') rather than a literal, because unlike a version number "New!" does not read
        // the same in every language. The toggle is ~182px wide: in a panel under 20rem (320px
        // at md, phones under ~350px) Inputs' toolbar drops Reset to its icon so the badge
        // isn't covered by the workflows button.
        { key: 'animate', label: t('video'), badge: t('new') },
      ].map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => {
            setSettingsTab(tab.key)
            // Mirror the choice onto c.formatTab so the cloneData persistence in
            // Create.jsx stores the last-used tab — otherwise a stale 'video'
            // (from a clone/animate hand-off) is restored on every refresh.
            c.setFormatTab(tab.key === 'animate' ? 'video' : 'photo')
          }}
          className={`flex-1 h-9 px-4 rounded-xl text-sm font-semibold transition-colors inline-flex items-center justify-center gap-1.5 ${
            settingsTab === tab.key
              ? 'bg-white text-black'
              : 'text-white hover:bg-[var(--foreground-bg-color)]'
          }`}
        >
          {tab.label}
          {tab.badge && (
            <span className="bg-[var(--main-bg-color)] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              {tab.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  )

  return (
    <>
      <div
        // The shell keeps its full height when the keyboard opens; only the
        // prompt bar rises, overlaying the feed (see PromptBar). Shrinking the
        // shell instead would reflow a snap-scrolling feed mid-typing, which
        // moves the card you're looking at.
        style={{ top: headerOffset }}
        className="fixed inset-x-0 bottom-0 z-30 flex w-full max-w-full min-h-0 overflow-hidden bg-[var(--background-bg-color)]"
      >
        {/* Desktop: always-visible side panel. Mobile: full-screen slide-over. */}
        <SettingsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          {settingsTab === 'image' ? (
            <Inputs
              hidePromptAndActions
              styleButtonResponsive
              headerLeft={modeToggle}
              {...inputsProps}
            />
          ) : (
            <InputsVideo
              hidePromptAndActions
              textToVideoOnly
              headerLeft={modeToggle}
              {...inputsVideoProps}
            />
          )}
        </SettingsDrawer>

        <div className="relative flex-1 min-w-0 min-h-0 overflow-hidden">
          {/* Phone-only top bar (below md, where the settings panel is an overlay
              rather than a static column), left-aligned to match that panel. */}
          <div className="md:hidden pointer-events-none absolute top-0 inset-x-0 z-20 flex items-center justify-start gap-2 px-4 py-3">
            <button
              type="button"
              onClick={() => setDrawerOpen((v) => !v)}
              aria-label={t('settings')}
              aria-pressed={drawerOpen}
              className={`pointer-events-auto h-11 w-11 flex items-center justify-center rounded-full transition-colors shadow-lg ${
                drawerOpen
                  ? 'bg-[var(--main-bg-color)] text-white'
                  : 'bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] text-white'
              }`}
            >
              <BsThreeDotsVertical size={18} />
            </button>
            <button
              type="button"
              onClick={() => setStyleOpen(true)}
              className="pointer-events-auto inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] text-white font-semibold transition-colors shadow-lg"
            >
              <span className="truncate max-w-[180px]">
                {getLocalizedModelLabel(c.model)}
              </span>
              <FaChevronDown size={12} className="opacity-70" />
            </button>
          </div>

          {/* Pinned top-right so they clear the left-aligned settings/model bar and
            sit in the dead space beside the centered feed. The history button shows
            once there are videos in the list OR a video is currently generating (so
            it's reachable the moment you start animating); download shows when there's
            something saveable. */}
          {(bridge.videoCount > 0 ||
            c.videoIsLoading ||
            bridge.downloadCount > 0) && (
            <div className="pointer-events-none absolute top-0 right-0 z-20 flex items-center justify-end gap-2 px-4 py-3">
              {(bridge.videoCount > 0 || c.videoIsLoading) && (
                <button
                  type="button"
                  onClick={bridge.onOpenVideoHistory}
                  title={t('videoProgress')}
                  aria-label={t('videoProgressAndHistory')}
                  className="pointer-events-auto h-11 w-11 flex items-center justify-center rounded-full bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] text-white transition-colors shadow-lg"
                >
                  <TbHistoryToggle size={19} />
                </button>
              )}

              {bridge.downloadCount > 0 && (
                <SaveAllButton bridge={bridge} t={t} />
              )}
            </div>
          )}

          {/* Phones snap per card (TikTok-style); md+ scrolls freely, matching the
              static settings panel that appears at the same point. */}
          <div
            ref={feedScrollRef}
            className="h-full w-full min-w-0 max-w-full overflow-x-hidden overflow-y-auto overscroll-y-contain px-3 md:pt-6 md:pb-48 max-md:snap-y max-md:snap-mandatory"
          >
            {/* Expose the current mode to the feed's empty state (built up in
                Create.jsx, so it can't read settingsTab directly). */}
            <GenerateModeContext.Provider
              value={settingsTab === 'animate' ? 'video' : 'image'}
            >
              {gallery}
            </GenerateModeContext.Provider>
          </div>

          {/* Mode-aware (single instance so the containerRef/ResizeObserver stays put
              across tab switches): Image tab generates images; the Video tab does
              text-to-video (film icon, "Video Prompt", video prompt/cost/loading). */}
          <PromptBar
            containerRef={promptBarRef}
            mode={settingsTab === 'animate' ? 'video' : 'image'}
            prompt={settingsTab === 'animate' ? c.videoPrompt : c.prompt}
            setPrompt={
              settingsTab === 'animate' ? c.setVideoPrompt : c.setPrompt
            }
            onGenerate={
              settingsTab === 'animate'
                ? bridge.onGenerateVideoText
                : bridge.onGenerate
            }
            // Disable only while the relevant generation runs.
            isGenerating={
              settingsTab === 'animate'
                ? c.videoIsLoading
                : bridge.imageAtConcurrencyLimit
            }
            gemCost={settingsTab === 'animate' ? bridge.videoGemCost : gemCost}
            charImage={
              settingsTab === 'animate' ? c.charImageVideo : c.charImage
            }
            onClearChar={() =>
              settingsTab === 'animate'
                ? c.setCharImageVideo(null)
                : c.setCharImage(null)
            }
          />
        </div>
      </div>

      {/* Rendered outside the z-30 create-view container so its z-50 isn't trapped
          below the header. */}
      <ModelModal
        isOpen={styleOpen}
        closeModal={() => setStyleOpen(false)}
        subscription={subscription?.plan}
        model={c.model}
        setModel={c.setModel}
        setLora={c.setLora}
        setFilter={c.setFilter}
        user={user}
        newUser={newUser}
      />
    </>
  )
}
